import { useState, useRef, useEffect } from "react";
import { PaperAirplaneIcon, StarIcon } from "@heroicons/react/24/solid";
import ReactMarkdown from "react-markdown";
import Confetti from "react-confetti";
import { generateContent } from "../utils/ai";
import { saveNote } from "../utils/supabase";
import styles from "../AskAI.module.css";

function AskAIBadge() {
  return (
    <div style={{
      position: 'fixed',
      left: 24,
      bottom: 18,
      zIndex: 40,
      background: 'rgba(99,102,241,0.08)',
      color: '#6366F1',
      fontWeight: 700,
      fontSize: '1.05rem',
      borderRadius: 10,
      padding: '7px 18px',
      boxShadow: '0 2px 8px #6366F122',
      letterSpacing: '0.04em',
      pointerEvents: 'none',
      userSelect: 'none',
      opacity: 0.85
    }}>AskAI</div>
  );
}

function AskInputBar({ value, onChange, onSend, loading, atBottom }) {
  return (
    <div className={atBottom ? styles.inputBarBottom : styles.inputBar}>
      <input
        className={styles.input}
        placeholder="Message here..."
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" && !loading) onSend();
        }}
        disabled={loading}
      />
      <button
        className={styles.sendBtn}
        onClick={onSend}
        aria-label="Send"
        disabled={loading}
      >
        <PaperAirplaneIcon style={{ width: 20, height: 20 }} />
      </button>
    </div>
  );
}

function AIResponse({ answer, loading, showDefault }) {
  return (
    <div className={styles.response}>
      <div className={styles.bubble}>
        {loading ? (
          <div style={{ height: 24, width: '50%', background: 'linear-gradient(90deg, #EEF2FF 25%, #e0e7ff 50%, #EEF2FF 75%)', borderRadius: 8, animation: 'shimmer 1.5s infinite linear', backgroundSize: '200% 100%' }} />
        ) : showDefault ? (
          <div>Hello! Ask me anything.</div>
        ) : (
          <div>
            <ReactMarkdown>{answer}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

function PastQuestions({ questions, onSelect }) {
  return (
    <div className={styles.pastQuestions}>
      <div className={styles.pastList}>
        {questions.map((q, i) => (
          <div
            key={i}
            className={styles.pastItem}
            onClick={() => onSelect(i)}
          >
            <div className={styles.pastQ}>{q.question}</div>
            <div className={styles.pastPreview}>
              {q.answer.slice(0, 50)}...
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BottomTools({ onCopy, onRegenerate, onShare, onSave }) {
  return (
    <div className={styles.bottomTools}>
      <button className={styles.toolBtn} onClick={onCopy}>Copy Answer</button>
      <button className={styles.toolBtn} onClick={onRegenerate}>Regenerate</button>
      <button className={styles.toolBtn} onClick={onShare}>Share</button>
      <button className={styles.toolBtn} onClick={onSave}><StarIcon style={{ width: 20, height: 20 }} /></button>
    </div>
  );
}

export default function AskAI() {
  const [input, setInput] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [easterEgg, setEasterEgg] = useState(false);
  const [saved, setSaved] = useState(false);
  const responseRef = useRef(null);

  useEffect(() => {
    if (responseRef.current) {
      responseRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [answer, loading]);

  useEffect(() => {
    if (answer && !loading) {
      const today = new Date().toISOString().slice(0, 10);
      const lastConfetti = localStorage.getItem("askai_confetti");
      if (lastConfetti !== today) {
        setShowConfetti(true);
        localStorage.setItem("askai_confetti", today);
        setTimeout(() => setShowConfetti(false), 3000);
      }
    }
  }, [answer, loading]);

  useEffect(() => {
    if (easterEgg) {
      const timer = setTimeout(() => setEasterEgg(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [easterEgg]);

  const handleSend = async () => {
    if (!input.trim()) return;
    if (loading) return;
    setLoading(true);
    setSaved(false);
    setEasterEgg(false);
    try {
      if (input.trim().toLowerCase() === "what is ix?") {
        setEasterEgg(true);
        setAnswer("IX is the study revolution. ✨");
        setQuestions(prev => [{ question: input, answer: "IX is the study revolution. ✨" }, ...prev]);
        setLoading(false);
        return;
      }
      const messages = [
        { role: "system", content: "You are a helpful, concise AI assistant. Answer any question clearly and helpfully." },
        { role: "user", content: input }
      ];
      const aiAnswer = await generateContent(messages);
      setAnswer(aiAnswer);
      setQuestions(prev => [{ question: input, answer: aiAnswer }, ...prev]);
    } catch (err) {
      setAnswer("Sorry, there was an error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (answer) navigator.clipboard.writeText(answer);
  };

  const handleRegenerate = async () => {
    if (!questions.length) return;
    setInput(questions[0].question);
    await handleSend();
  };

  const handleShare = () => {
    if (answer) {
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(answer)}`;
      window.open(url, "_blank");
    }
  };

  const handleSave = async () => {
    try {
      await saveNote("anonymous", answer, questions[0]?.question || "AskAI Note");
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert("Failed to save note");
    }
  };

  const showDefault = !answer && !loading && questions.length === 0;

  return (
    <div className={styles.root}>
      {showConfetti && <div className={styles.confettiOverlay}><Confetti recycle={false} numberOfPieces={300} /></div>}
      {easterEgg && (
        <div className={styles.easterEgg}>
          <div className={styles.easterEggBox}>
            🎉 IX is the study revolution! 🎉
          </div>
        </div>
      )}
      <main className={styles.main}>
        <div ref={responseRef} />
        <AIResponse answer={answer} loading={loading} showDefault={showDefault} />
        <PastQuestions questions={questions} onSelect={i => {
          setInput(questions[i].question);
          setAnswer(questions[i].answer);
        }} />
      </main>
      <AskInputBar value={input} onChange={setInput} onSend={handleSend} loading={loading} atBottom={true} />
      <BottomTools
        onCopy={handleCopy}
        onRegenerate={handleRegenerate}
        onShare={handleShare}
        onSave={handleSave}
      />
      {saved && (
        <div className={styles.savedNote}>
          Note saved!
        </div>
      )}
      <AskAIBadge />
    </div>
  );
}
